import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe, ChevronDown } from "lucide-react";
import { useLang } from "../context/LanguageContext";

export default function Navbar() {
  const { t, toggleLang, dir } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openDrop, setOpenDrop] = useState(null);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);
  const isHome = loc.pathname === "/";
  const lightHeader = scrolled || open;

  const serviceLinks = t.servicesList.map((s) =>
    s.slug === "cyber-security"
      ? { to: "https://cyberquess.com/", label: s.title, external: true }
      : { to: `/services/${s.slug}`, label: s.title }
  );
  const resourceLinks = [
    { to: "/blog", label: t.res.menu.blogs },
    { to: "/case-studies", label: t.res.menu.caseStudies },
    { to: "/platforms", label: t.res.menu.platforms },
  ];

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/about", label: t.nav.about },
    { to: "/why-us", label: t.nav.why },
    { to: "/services", label: t.nav.services, dropdown: "services" },
    { to: "/blog", label: t.nav.resources, dropdown: "resources" },
    { to: "/careers", label: t.nav.careers },
  ];

  const isActive = (l) =>
    l.dropdown === "services"
      ? loc.pathname.startsWith("/services")
      : l.dropdown === "resources"
      ? ["/blog", "/case-studies", "/platforms"].some((p) => loc.pathname.startsWith(p))
      : loc.pathname === l.to;

  const dropItems = (key) => (key === "services" ? serviceLinks : resourceLinks);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={{
        background: lightHeader ? "rgba(238,245,240,0.97)" : "transparent",
        backdropFilter: lightHeader ? "blur(12px)" : "none",
        borderBottom: "1px solid transparent",
      }}
    >
      <nav dir="ltr" className="flex items-center justify-between h-[74px] sm:h-[84px] lg:h-[94px] px-4 sm:px-6 lg:px-10 w-full">
        <Link to="/" className="flex items-center gap-2 group shrink-0">
          <img
            src="/roboquess-logo-new.webp"
            alt="RoboQuess"
            className="h-11 sm:h-12 lg:h-16 object-contain transition-all duration-300 group-hover:scale-105"
            style={{ filter: (!lightHeader && isHome) ? "brightness(0) invert(1)" : "none" }}
          />
        </Link>

        <div className="flex items-center gap-3 ml-auto">
          <div className="hidden lg:flex items-center gap-1 rounded-full px-3 py-2" style={{ background: "rgba(9,26,18,0.55)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.12)" }}>
            {links.map((l) => {
              const active = isActive(l);
              if (l.dropdown) {
                const isOpen = openDrop === l.dropdown;
                return (
                  <div key={l.to} className="relative" onMouseEnter={() => setOpenDrop(l.dropdown)} onMouseLeave={() => setOpenDrop(null)}>
                    <Link
                      to={l.to}
                      className="relative flex items-center gap-1 px-4 py-1.5 text-[13px] font-medium uppercase tracking-wide transition-colors duration-300"
                      style={{ color: active || isOpen ? "#7be0a8" : "rgba(255,255,255,0.85)" }}
                    >
                      {l.label}
                      <ChevronDown size={13} className="transition-transform duration-300" style={{ transform: isOpen ? "rotate(180deg)" : "none" }} />
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] rounded-full transition-all duration-300" style={{ width: active ? "60%" : "0%", background: "#7be0a8" }} />
                    </Link>
                    <div
                      className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-64 z-50"
                      style={{
                        visibility: isOpen ? "visible" : "hidden",
                        opacity: isOpen ? 1 : 0,
                        transform: isOpen ? "translateX(-50%) translateY(0)" : "translateX(-50%) translateY(8px)",
                        transition: "opacity 0.25s ease, transform 0.25s ease",
                      }}
                    >
                      <div className="rounded-2xl p-2 grid divide-y divide-white/10" style={{ background: "rgba(6,20,15,0.97)", border: "1px solid rgba(255,255,255,0.12)", backdropFilter: "blur(14px)", boxShadow: "0 30px 60px -30px rgba(0,0,0,0.75)" }}>
                        {dropItems(l.dropdown).map((s) =>
                          s.external ? (
                            <a key={s.to} href={s.to} target="_blank" rel="noopener noreferrer" className="rounded-lg px-3 py-2 text-[12.5px] font-medium normal-case tracking-normal text-white/80 hover:text-[#7be0a8] hover:bg-[rgba(46,230,110,0.08)] transition-colors">
                              {s.label}
                            </a>
                          ) : (
                            <Link key={s.to} to={s.to} className="rounded-lg px-3 py-2 text-[12.5px] font-medium normal-case tracking-normal text-white/80 hover:text-[#7be0a8] hover:bg-[rgba(46,230,110,0.08)] transition-colors" style={loc.pathname === s.to ? { color: "#7be0a8" } : undefined}>
                              {s.label}
                            </Link>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <Link key={l.to} to={l.to} className="relative px-4 py-1.5 text-[13px] font-medium uppercase tracking-wide transition-colors duration-300" style={{ color: active ? "#7be0a8" : "rgba(255,255,255,0.85)" }}>
                  {l.label}
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] rounded-full transition-all duration-300" style={{ width: active ? "60%" : "0%", background: "#7be0a8" }} />
                </Link>
              );
            })}
            <button onClick={toggleLang} className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold text-white/85 hover:text-[#7be0a8] transition-colors" aria-label="toggle language">
              <Globe size={15} />
              <span className="inline-flex items-center leading-none" style={t.langLabel === "ع" ? { fontSize: "17px", transform: "translateY(-1px)" } : undefined}>{t.langLabel}</span>
            </button>
            <Link to="/contact" className="inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2 text-[13px] font-bold uppercase tracking-wide text-[color:var(--rq-navy)] transition-all duration-300 hover:bg-[#7be0a8]">
              {t.nav.contact}
            </Link>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <button onClick={toggleLang} className="inline-flex items-center justify-center gap-1.5 h-10 rounded-full border px-3.5 text-sm font-semibold transition-colors" aria-label="toggle language" style={{ color: (!lightHeader && isHome) ? "#fff" : "var(--rq-green)", borderColor: (!lightHeader && isHome) ? "rgba(255,255,255,0.4)" : "rgba(14,122,58,0.4)" }}>
              <Globe size={16} />
              <span className="inline-flex items-center leading-none" style={t.langLabel === "ع" ? { fontSize: "18px", transform: "translateY(-1px)" } : undefined}>{t.langLabel}</span>
            </button>
            <button className="grid place-items-center h-10 w-10 rounded-full transition-colors" onClick={() => setOpen((o) => !o)} aria-label="menu" style={{ color: open ? "var(--rq-green)" : ((!lightHeader && isHome) ? "#fff" : "var(--rq-navy)"), background: open ? "rgba(14,122,58,0.1)" : "transparent" }}>
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* mobile menu */}
      <div className="lg:hidden overflow-hidden transition-all duration-500" style={{ maxHeight: open ? "calc(100vh - 74px)" : "0px", overflowY: open ? "auto" : "hidden", background: "#ffffff", backdropFilter: "blur(14px)", borderBottom: open ? "1px solid rgba(14,122,58,0.14)" : "none" }}>
        <div className="px-4 sm:px-6 py-4 flex flex-col gap-1">
          {links.map((l) => (
            <React.Fragment key={l.to}>
              <Link to={l.to} className="py-3 px-2 text-base font-medium border-b border-black/5" style={{ color: isActive(l) ? "var(--rq-green)" : "#334155" }}>
                {l.label}
              </Link>
              {l.dropdown && (
                <div className="flex flex-col pl-3 border-b border-black/5">
                  {dropItems(l.dropdown).map((s) =>
                    s.external ? (
                      <a key={s.to} href={s.to} target="_blank" rel="noopener noreferrer" className="py-2 px-2 text-sm" style={{ color: "#64748b" }}>
                        {s.label}
                      </a>
                    ) : (
                      <Link key={s.to} to={s.to} className="py-2 px-2 text-sm" style={{ color: loc.pathname === s.to ? "var(--rq-green)" : "#64748b" }}>
                        {s.label}
                      </Link>
                    )
                  )}
                </div>
              )}
            </React.Fragment>
          ))}
          <Link to="/contact" className="mt-4 rq-glow-btn text-center py-3.5">
            {t.nav.cta}
          </Link>
        </div>
      </div>
    </header>
  );
}
